import React, { useState, useEffect, useRef } from 'react';
import { TextField, Box, List, ListItem, ListItemText, ListItemAvatar, Avatar, Typography, Chip, Paper, InputAdornment, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getFullApiUrl } from '../utils/api';

interface Author {
  username: string;
  avatar?: string;
}

interface SearchResult {
  id: string;
  _id?: string; // Keep for backward compatibility
  title: string;
  content: string;
  author: Author;
  category: string;
  tags: string[];
  upvotes: any[];
  downvotes: any[];
  views: number;
  commentsCount: number;
  createdAt: string;
  relevanceScore?: number;
  translations?: {
    title?: string;
    content?: string;
  };
}

interface SearchBoxProps {
  onSearch?: (searchTerm: string) => void;
}

const SearchBox: React.FC<SearchBoxProps> = ({ onSearch }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout>();

  const performSearch = async (term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      console.log('Performing search for:', term);





      console.log('[SEARCH DEBUG] API Base URL:', getFullApiUrl('/api/opinions'));

      const response = await axios.get(getFullApiUrl('/api/opinions'), {
        params: {
          search: term,
          limit: 8,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        }
      });

      console.log('Search response:', response.data);
      const data = response.data as { opinions: any[] };
      setSearchResults(data.opinions || []);
      setShowResults(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    setSearchTerm(term);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      performSearch(term);
    }, 300);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (onSearch) {
      onSearch(searchTerm);
    }
    setShowResults(false);
  };

  const handleResultClick = (opinionId: string) => {
    navigate(`/opinion/${opinionId}`);
    setShowResults(false);
    setSearchTerm('');
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return t('time.justNow');
    if (diffInHours < 24) return t('time.hoursAgo', { hours: diffInHours });

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return t('time.daysAgo', { days: diffInDays });

    return date.toLocaleDateString();
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      politics: '#e91e63',
      technology: '#2196f3',
      health: '#4caf50',
      education: '#ff9800',
      environment: '#8bc34a',
      sports: '#f44336',
      entertainment: '#9c27b0',
      other: '#607d8b'
    };
    return colors[category] || colors.other;
  };

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: 600 }} ref={searchRef}>
      <form onSubmit={handleSearchSubmit}>
        <TextField
          fullWidth
          placeholder={t('search.placeholder') as string}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSearchSubmit(event);
            }
          }}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'background.paper',
            }
          }}
        />
      </form>

      {(showResults || (onSearch && searchTerm.trim())) && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: 400,
            overflow: 'auto',
            mt: 1,
            borderRadius: 2,
          }}
        >
          {searchResults.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {isSearching ? t('search.searching') : searchTerm.trim() ? t('search.noResults') : t('search.placeholder')}
              </Typography>
            </Box>
          ) : (
            <List dense>
              {searchResults.map((result) => (
                <ListItem
                  key={result._id}
                  button
                  onClick={() => handleResultClick(result.id || result._id!)}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={result.author?.avatar}
                      alt={result.author?.username}
                      sx={{ width: 32, height: 32 }}
                    >
                      {result.author?.username?.charAt(0).toUpperCase() || '?'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        {result.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {result.content.length > 100
                            ? `${result.content.substring(0, 100)}...`
                            : result.content}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            {result.author?.username || 'Unknown'}
                          </Typography>
                          <Chip
                            label={t(`categories.${result.category}`)}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: getCategoryColor(result.category),
                              color: 'white',
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(result.createdAt)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {result.upvotes.length - result.downvotes.length} {t('opinion.score')}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SearchBox;